import { useEffect, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import styles from './ReloadDock.module.css';

interface IReloadDockProps {
  icon: ReactNode;
  title: string;
  sub: string;
  onReload: () => void;
  /** A reload is running: the button waits. */
  busy?: boolean;
  /** Portal target when <body> is hidden (a page in native fullscreen) —
   *  the same contract as `Modal`'s `container`. */
  container?: HTMLElement | null;
}

/**
 * Someone else changed what this page shows: editing is locked until the page
 * loads the latest version. One dock for the whole page, portaled to <body> on
 * the toast layer so it floats above every dialog/drawer backdrop.
 *
 * Working over an open Base UI modal needs no escape hatch, only two facts
 * about @base-ui/react 1.4: (1) a modal dialog marks everything outside its
 * portal aria-hidden EXCEPT `[aria-live]` regions (markOthers in
 * floating-ui-react/utils), hence the explicit `aria-live` — a bare
 * role="status" would drop out of the a11y tree if the dock predates the
 * modal; (2) a dialog rendered with a Backdrop dismisses on an outside press
 * only when the press lands ON that backdrop (useDialogRoot `outsidePress`),
 * so pressing the dock never closes it. No inert is applied and focus is not
 * pulled back, so the open form keeps its input.
 */
export function ReloadDock({ icon, title, sub, onReload, busy = false, container }: IReloadDockProps) {
  const { t } = useTranslation();
  const dockRef = useRef<HTMLDivElement>(null);
  // Raise the toast stack by the dock's rendered height (it grows when the
  // message wraps); Toasts.module.css adds the slot gap.
  useEffect(() => {
    const dock = dockRef.current;
    if (!dock) return undefined;
    const root = document.documentElement;
    const lift = (height: number) => root.style.setProperty('--toast-lift-h', `${height}px`);
    lift(dock.getBoundingClientRect().height);
    const observer = new ResizeObserver((entries) => lift(entries[0]!.borderBoxSize[0]!.blockSize));
    observer.observe(dock);
    return () => {
      observer.disconnect();
      root.style.removeProperty('--toast-lift-h');
    };
  }, []);
  return createPortal(
    <div ref={dockRef} className={styles.dock} role="status" aria-live="polite">
      <span className={styles.icon}>
        {icon}
      </span>
      <div className={styles.message}>
        <span className={styles.title}>{title}</span>
        <span className={styles.sub}>{sub}</span>
      </div>
      <button type="button" className={styles.action} onClick={onReload} disabled={busy} aria-busy={busy}>
        {t('common.load')}
      </button>
    </div>,
    container ?? document.body,
  );
}
